import { useState, useEffect } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import { AxiosError } from 'axios';

import { Button } from '@/components/button';
import WalletConnectButton from '@/components/layout/header/wallet-connect/WalletConnectButton';

import { getTitle, REFERRAL } from '../utils';

import Icons from './Icons';
import InputErrorMessage from './InputErrorMessage';

interface ConnectWalletProps {
  isButtonBlocked: boolean;
  isWalletConnected: boolean;
  isRegistered: boolean;
  isCheckLoading: boolean;
  isLoginLoading: boolean;
  isRegisterPending: boolean;
  registerError: AxiosError<{ error: string }> | null;
  login: VoidFunction;
  register: (referralCode: string) => void;
  onErrorReset: VoidFunction;
}

const ConnectWallet: React.FC<ConnectWalletProps> = ({
  isButtonBlocked,
  isWalletConnected,
  isRegistered,
  isCheckLoading,
  isLoginLoading,
  isRegisterPending,
  registerError,
  login,
  register,
  onErrorReset,
}: ConnectWalletProps) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const referralId = searchParams.get(REFERRAL);

  const isCombinedLoading = isCheckLoading || isLoginLoading || isRegisterPending;

  // using state as a second source of truth does not block input
  const [referralCode, setReferralCode] = useState<string>(referralId || '');

  useEffect(() => {
    if (referralId) {
      setReferralCode(referralId);
    }
  }, [referralId]);

  const handleClearInput = () => {
    setReferralCode('');
    updateUrl('');
  };

  const handleInputChange = (value: string) => {
    setReferralCode(value);
    updateUrl(value);

    if (registerError) {
      onErrorReset();
    }
  };

  const updateUrl = (value: string) => {
    const params = new URLSearchParams(searchParams.toString());

    if (value) {
      params.set(REFERRAL, value);
    } else {
      params.delete(REFERRAL);
    }

    router.replace(`/quanta?${params.toString()}`);
  };

  const handleRegister = () => {
    register(referralCode);
  };

  return (
    <div className='flex flex-col items-center justify-center max-w-3xl h-fit gap-4 lg:gap-6 p-4 lg:p-10 rounded-lg lg:rounded-[20px] bg-surface-secondary'>
      <h1 className='text-center text-2xl lg:text-[40px] font-semibold text-text-primary'>
        Explorer, connect your wallet to take the next step in your Ducat journey.
      </h1>
      <p className='text-center lg:text-2xl text-text-secondary'>
        Complete missions and collect Quanta - experience points that help you climb the leaderboard and boost your standing in the Ducat
        community.
      </p>
      {isWalletConnected && !isRegistered && !isCheckLoading && (
        <div className='flex flex-col gap-1 w-full max-w-md'>
          <div className='flex items-center gap-2'>
            <Icons.Referral />
            <label htmlFor='invitation-code' className='text-lg text-text-primary'>
              Apply referral code <span className='text-text-secondary'>(optional)</span>
            </label>
          </div>
          <div className='relative'>
            <input
              id='invitation-code'
              type='text'
              placeholder='Invitation code'
              maxLength={16}
              value={referralCode}
              onChange={(e) => handleInputChange(e.target.value)}
              className='w-full px-4 py-2 bg-surface-primary rounded-lg text-text-primary placeholder:text-text-secondary focus:outline-none focus:ring-1 focus:ring-border-secondary'
              autoComplete='off'
              aria-label='Invitation code input'
            />
            {referralCode && (
              <button
                aria-label='Clear input'
                type='button'
                className='absolute top-1/2 right-2 -translate-y-1/2 p-[6px] rounded-full transition-colors hover:bg-surface-secondary'
                onClick={handleClearInput}
              >
                <Icons.Close className='w-[19px] h-[19px] text-text-secondary' />
              </button>
            )}
          </div>
          {!!registerError && <InputErrorMessage message={registerError.response?.data?.error ?? registerError.message} />}
        </div>
      )}
      {isWalletConnected ? (
        <Button
          variant='secondary'
          loading={isCombinedLoading}
          disabled={isCombinedLoading || isButtonBlocked}
          className='min-w-[176px] min-h-[40px]'
          onClick={isRegistered ? login : handleRegister}
        >
          {getTitle(isWalletConnected, isRegistered)}
        </Button>
      ) : (
        <WalletConnectButton mobileStylesOn={false} />
      )}
    </div>
  );
};

export default ConnectWallet;
